import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faBars,
  faSignOutAlt,
  faEnvelope,
  faLocationDot,
  faFileInvoice,
  faUserPlus,
  faSync,
  faSearch,
  faUser,
  faQuestionCircle,
  faInfoCircle,
  faMapMarkerAlt,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { APP_ROUTES } from '../config/api.config';
import Loader from '../components/Loader';
import { getSubscribedLocations, cancelSubscription, cancelPendingSubscription, initiateGHLAuth, getSubscriptionPlans, createSubscription, createTestSubscription, disconnectWhatsApp, relinkSubAccountLocation, restoreSubAccountLocation, restoreSubAccountViaCreate, ApiEndpointUnavailableError, Plan } from '../services/api';
import { filterSubscriptionPlans, planMatchesPeriod, isSingleSubAccountPlan, PLAN_FEATURE_LIST, formatPlanPrice, getPlanDisplayAmount, isPlanCardCurrent, getPaidSubscriptions, isTestSubscription } from '../utils/subscriptionPlans';
import { isPaidSubscriptionStatus, isPendingSubscriptionStatus } from '../utils/subscriptionStatus';
import Footer from '../components/Footer';
import noDataFound from '../assets/images/no-data-found.png';
import noResults from '../assets/images/no-result.jpg';
import { useLogoutHandler } from '../utils/logoutHandler';
import LogoutConfirmDialog from '../components/LogoutConfirmDialog';
import { toast } from 'react-hot-toast';
import Sidebar from '../components/Sidebar/Sidebar';
import WhatsAppLogoutDialog from '../components/WhatsAppLogoutDialog';
import theme from '../styles/theme';

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${theme.colors.bgMesh};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MobileHeader = styled.div`
  display: none;
`;

const HamburgerButton = styled.button`
  background: none;
  border: none;
  color: #333;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.3s ease;
  border: 1px solid #e0e0e0;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
`;

const MainContent = styled.div<{ isCollapsed: boolean; isMobileOpen: boolean }>`
  flex: 1;
  margin-left: ${props => (props.isCollapsed ? '72px' : '260px')};
  padding: 0;
  padding-bottom: 60px;
  padding-top: 80px;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: calc(100% - ${props => (props.isCollapsed ? '72px' : '260px')});

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding-top: 80px;
    transition: none;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  padding: 12px;
  background: #f8d7da;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const TopBar = styled.div<{ isCollapsed: boolean; isMobileOpen: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 28px;
  background: ${theme.glass.light};
  backdrop-filter: ${theme.glass.blur};
  -webkit-backdrop-filter: ${theme.glass.blur};
  border-bottom: 1px solid ${theme.colors.cardBorder};
  box-shadow: ${theme.shadows.topbar};
  margin-bottom: 28px;
  position: fixed;
  top: 0;
  right: 0;
  left: ${props => (props.isCollapsed ? '72px' : '260px')};
  z-index: 100;
  height: ${theme.layout.topbarHeight}px;
  transition: left ${theme.transitions.base};

  @media (max-width: 768px) {
    left: 0;
    width: 100%;
    padding: 14px 20px;
    transition: none;
  }
`;

const ToggleButton = styled.button`
  background: ${theme.colors.card};
  border: 1px solid ${theme.colors.cardBorder};
  color: ${theme.colors.textSecondary};
  font-size: 18px;
  cursor: pointer;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.radius.md};
  transition: all 0.2s ease;
  box-shadow: ${theme.shadows.sm};

  &:hover {
    background: rgba(255, 107, 107, 0.08);
    border-color: rgba(255, 107, 107, 0.3);
    color: ${theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  cursor: pointer;
  padding: 6px 10px 6px 16px;
  border-radius: ${theme.radius.pill};
  border: 1px solid ${theme.colors.cardBorder};
  background: ${theme.colors.card};
  transition: all 0.2s ease;
  box-shadow: ${theme.shadows.sm};

  &:hover {
    border-color: rgba(255, 107, 107, 0.35);
    box-shadow: ${theme.shadows.md};
  }

  @media (max-width: 768px) {
    padding: 6px 10px;
    gap: 8px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.text};
  line-height: 1.2;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const UserAvatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${theme.colors.accentGradient};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.35);
`;

const UserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: ${theme.colors.card};
  border-radius: ${theme.radius.lg};
  border: 1px solid ${theme.colors.cardBorder};
  box-shadow: ${theme.shadows.lg};
  min-width: 200px;
  display: ${props => (props.isOpen ? 'block' : 'none')};
  z-index: 1000;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: ${theme.colors.textSecondary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.06);
    color: ${theme.colors.primary};
  }

  svg {
    width: 16px;
    margin-right: 12px;
    opacity: 0.7;
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background-color: #e8e8e8;
  margin: 4px 0;
`;

const UserEmail = styled.div`
  font-size: 15px;
  color: #666;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    width: 16px;
    color: #666;
  }
`;

const LogoutMenuItem = styled.button<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: white;
  font-size: 15px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 20px;
    margin-right: ${props => props.$isCollapsed ? '0' : '12px'};
  }

  span {
    display: ${props => props.$isCollapsed ? 'none' : 'block'};
  }
`;

const PageContainer = styled.div`
  background: ${theme.colors.card};
  border-radius: ${theme.radius.xl};
  padding: 28px;
  box-shadow: ${theme.shadows.card};
  border: 1px solid ${theme.colors.cardBorder};
  overflow-x: auto;
  margin: 0 24px 32px 24px;
  width: calc(100% - 48px);

  @media (max-width: 768px) {
    padding: 20px 16px;
    border-radius: ${theme.radius.lg};
    margin: 0 16px 24px 16px;
    width: calc(100% - 32px);
  }
`;

const TableTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${theme.colors.text};
  margin: 0 0 20px 0;
  letter-spacing: -0.03em;
`;

const TableControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 992px) {
    gap: 12px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const EntriesControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
`;

const TableHeader = styled.div`
  margin-bottom: 24px;
`;

const Select = styled.select`
  padding: 8px 14px;
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.radius.sm};
  background: ${theme.colors.card};
  color: ${theme.colors.textSecondary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.12);
  }
`;

const SearchControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 8px 36px 8px 14px;
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.radius.sm};
  min-width: 220px;
  font-size: 14px;
  background: ${theme.colors.card};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 42%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #aaa;
  font-size: 16px;
  cursor: pointer;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  &:hover {
    color: #ff6b6b;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  table-layout: fixed;

  @media (max-width: 992px) {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 14px 16px;
  border-bottom: 1px solid ${theme.colors.cardBorder};
  color: ${theme.colors.textMuted};
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  cursor: pointer;
  background: rgba(248, 250, 252, 0.8);
  transition: color 0.15s;

  &:hover {
    color: ${theme.colors.primary};
  }
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  color: ${theme.colors.textSecondary};
  font-size: 14px;
  word-break: break-word;
  vertical-align: middle;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 5px 12px;
  border-radius: ${theme.radius.pill};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: white;
  background: ${props => {
    switch (props.status.toLowerCase()) {
      case 'active':
        return 'linear-gradient(135deg, #10b981, #059669)';
      case 'authenticated':
        return 'linear-gradient(135deg, #0ea5e9, #0284c7)';
      case 'cancelled':
        return 'linear-gradient(135deg, #ef4444, #dc2626)';
      case 'created':
      case 'pending':
        return 'linear-gradient(135deg, #f59e0b, #d97706)';
      case 'failed':
        return 'linear-gradient(135deg, #ef4444, #b91c1c)';
      default:
        return 'linear-gradient(135deg, #64748b, #475569)';
    }
  }};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 30px 0;
  flex-wrap: wrap;
  width: 100%;

  @media (max-width: 768px) {
    margin: 20px 0;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
    flex-wrap: wrap;
    justify-content: center;
  width: 100%;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const PaginationButton = styled.button<{ active?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${props => props.active ? '#ff6b6b' : '#dee2e6'};
  background: ${props => props.active ? '#ff6b6b' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  min-width: 36px;

  &:hover {
    background: ${props => props.active ? '#ff6b6b' : '#fff0f0'};
    border-color: #ff6b6b;
    color: ${props => props.active ? 'white' : '#ff6b6b'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: #f8f9fa;
    border-color: #dee2e6;
    color: #666;
  }

  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 13px;
    min-width: 32px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin: 24px 0;
`;

const EmptyStateImage = styled.img`
  width: 660px;
  height: auto;
  margin-bottom: 24px;
`;

const EmptyImage = styled.img`
    width: 70%;
  height: auto;
  margin-bottom: 24px;
`;

const ErrorImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin: 24px 0;
`;

const TableActionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 168px;
  max-width: 200px;
`;

const TableActionButton = styled.button<{ variant?: 'connect' | 'disconnect' | 'cancel' | 'resubscribe' }>`
  width: 100%;
  min-height: 36px;
  padding: 8px 14px;
  border-radius: ${theme.radius.sm};
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  text-align: center;
  box-sizing: border-box;

  ${({ variant = 'connect' }) => {
    switch (variant) {
      case 'connect':
        return `
          background: ${theme.colors.accentGradient};
          color: #fff;
          border: none;
          box-shadow: 0 4px 12px rgba(255, 107, 107, 0.28);
          &:hover:not(:disabled) {
            box-shadow: ${theme.shadows.glow};
            transform: translateY(-1px);
          }
          &:active:not(:disabled) {
            transform: translateY(0);
          }
        `;
      case 'disconnect':
        return `
          background: linear-gradient(135deg, ${theme.colors.primaryDark} 0%, ${theme.colors.primary} 55%, ${theme.colors.secondary} 100%);
          color: #fff;
          border: none;
          box-shadow: 0 4px 14px rgba(255, 107, 107, 0.35);
          &:hover:not(:disabled) {
            box-shadow: ${theme.shadows.glow};
            transform: translateY(-1px);
          }
          &:active:not(:disabled) {
            transform: translateY(0);
          }
        `;
      case 'cancel':
        return `
          background: transparent;
          color: ${theme.colors.textMuted};
          border: 1.5px solid ${theme.colors.cardBorder};
          &:hover:not(:disabled) {
            border-color: ${theme.colors.primary};
            color: ${theme.colors.primary};
            background: rgba(255, 107, 107, 0.04);
          }
        `;
      case 'resubscribe':
        return `
          background: linear-gradient(135deg, #10b981, #059669);
          color: #fff;
          border: none;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.28);
          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 6px 16px rgba(16, 185, 129, 0.35);
          }
        `;
      default:
        return '';
    }
  }}

  &:disabled {
    background: #f1f5f9 !important;
    color: #94a3b8 !important;
    border-color: #e2e8f0 !important;
    cursor: not-allowed;
    box-shadow: none !important;
  }
`;

const CancelButton = styled.button`
  background: linear-gradient(135deg, #ff6b6b, #ff8e53);
  color: white;
  padding: 8px 20px;
  border-radius: 25px;
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  margin: 0 4px;

  &:hover {
    background: #ff8e53;
    color: #fff;
    box-shadow: 0 2px 8px rgba(255, 107, 107, 0.15);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
  animation: fadeIn 0.3s ease-out;
  padding: 20px;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    width: 95%;
    padding: 20px;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #edf2f7;
`;

const ModalTitle = styled.h2`
  color: #2d3748;
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #718096;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background: #f7fafc;
    color: #ff6b6b;
  }
`;

const OuterBox = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: calc(100% - 40px);
  max-width: 100%;
  margin: 24px 20px;
  overflow-x: visible;

  @media (max-width: 1200px) {
    padding: 20px;
    margin: 24px 15px;
    width: calc(100% - 30px);
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 8px;
    margin: 24px 15px;
    width: calc(100% - 30px);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 2rem;
  width: 100%;
  max-width: 100%;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const FormContainer = styled.div`
  background: ${theme.colors.card};
  border-radius: ${theme.radius.xl};
  padding: 32px;
  box-shadow: ${theme.shadows.card};
  border: 1px solid ${theme.colors.cardBorder};
  margin: 0 24px 32px 24px;
  width: calc(100% - 48px);
  position: relative;
  overflow: hidden;
  transition: box-shadow ${theme.transitions.base};

  &:hover {
    box-shadow: ${theme.shadows.cardHover};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${theme.colors.accentGradient};
  }

  @media (max-width: 768px) {
    padding: 24px 20px;
    margin: 0 16px 24px 16px;
    width: calc(100% - 32px);
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 14px 18px;
  border: 1.5px solid ${props => (props.hasError ? theme.colors.primary : theme.colors.cardBorder)};
  border-radius: ${theme.radius.md};
  font-size: 15px;
  transition: all 0.2s ease;
  background: #fafbfc;
  max-width: 520px;
  color: ${theme.colors.text};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    background: #fff;
    box-shadow: 0 0 0 4px rgba(255, 107, 107, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    background: #f1f5f9;
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  color: ${theme.colors.textSecondary};
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  letter-spacing: -0.01em;
`;

const PaymentNotice = styled.div<{ $variant: 'warning' | 'info' }>`
  margin-bottom: 24px;
  padding: 18px 22px;
  border-radius: ${theme.radius.lg};
  border: 1px solid ${props => (props.$variant === 'warning' ? 'rgba(245,158,11,0.35)' : 'rgba(14,165,233,0.3)')};
  background: ${props =>
    props.$variant === 'warning'
      ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)'
      : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'};
  color: ${props => (props.$variant === 'warning' ? '#92400e' : '#1e40af')};
  font-size: 14px;
  line-height: 1.6;
  max-width: 520px;
  box-shadow: ${theme.shadows.sm};

  strong {
    display: block;
    margin-bottom: 6px;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.02em;
  }
`;

const InfoText = styled.p`
  color: #718096;
  font-size: 1rem;
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 500px;
  width: 100%;

  svg {
    color: #ff6b6b;
    font-size: 0.9rem;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    max-width: 100%;
  }
`;

const ErrorText = styled.p`
  color: #ff6b6b;
  font-size: 0.95rem;
  margin-top: 0.75rem;
  width: 100%;
  max-width: 520px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
  max-width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1rem;
    width: 100%;
    padding: 0 5px;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 28px;
  border-radius: ${theme.radius.pill};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  white-space: nowrap;
  letter-spacing: -0.01em;

  ${props =>
    props.variant === 'primary'
      ? `
    background: ${theme.colors.accentGradient};
    color: white;
    border: none;
    box-shadow: 0 4px 14px rgba(255, 107, 107, 0.35);

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: ${theme.shadows.glow};
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  `
      : `
    background: transparent;
    color: ${theme.colors.textMuted};
    border: 1.5px solid ${theme.colors.cardBorder};

    &:hover {
      border-color: ${theme.colors.primary};
      color: ${theme.colors.primary};
      background: rgba(255,107,107,0.04);
    }
  `}

  @media (max-width: 768px) {
    width: 100%;
    padding: 12px 20px;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 2rem;
  border-radius: 25px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  
  ${props => props.variant === 'primary' ? `
    background: #ff6b6b;
    color: white;
    border: none;
    
    &:hover {
      background: #ff8e53;
    }
  ` : `
    background: transparent;
    color: #4a5568;
    border: none;
    
    &:hover {
      color: #2d3748;
    }
  `}

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.625rem 1.5rem;
    font-size: 0.9rem;
  }
`;

const PlansList = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const PlanItem = styled.div<{ $highlight?: boolean; $isCurrent?: boolean }>`
  border: 2px solid ${props =>
    props.$isCurrent
      ? theme.colors.primary
      : props.$highlight
        ? 'rgba(255, 107, 107, 0.35)'
        : theme.colors.cardBorder};
  border-radius: ${theme.radius.xl};
  padding: 2rem 1.75rem;
  flex: 1 1 300px;
  max-width: 400px;
  min-width: 280px;
  background: ${props => (props.$isCurrent ? 'rgba(255, 107, 107, 0.04)' : theme.colors.card)};
  box-shadow: ${props =>
    props.$isCurrent ? theme.shadows.lg : props.$highlight ? theme.shadows.lg : theme.shadows.card};
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.colors.accentGradient};
    opacity: ${props => (props.$highlight || props.$isCurrent ? 1 : 0)};
    transition: opacity 0.3s;
  }

  &:hover {
    transform: ${props => (props.$isCurrent ? 'none' : 'translateY(-4px)')};
    box-shadow: ${props =>
      props.$isCurrent
        ? theme.shadows.lg
        : `${theme.shadows.lg}, 0 0 0 1px rgba(255, 107, 107, 0.12)`};
    border-color: ${props => (props.$isCurrent ? theme.colors.primary : 'rgba(255, 107, 107, 0.25)')};

    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    min-width: 100%;
    max-width: 100%;
    padding: 1.5rem;
  }
`;

const PlansModalOuter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const PlansModalInner = styled.div`
  background: ${theme.colors.card};
  border-radius: ${theme.radius.xl};
  box-shadow: ${theme.shadows.card};
  border: 1px solid ${theme.colors.cardBorder};
  padding: 36px 32px 32px;
  width: 100%;
  position: relative;
  overflow: auto;
  max-height: none;
`;

const PlansSectionHeader = styled.div`
  text-align: center;
  margin-bottom: 28px;
  padding-right: 48px;
`;

const PlansSectionTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 26px;
  font-weight: 800;
  color: ${theme.colors.text};
  letter-spacing: -0.03em;

  span {
    background: ${theme.colors.accentGradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const PlansSectionSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${theme.colors.textMuted};
`;

const DemoPlansNotice = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.primary};
`;

const PlanCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  font-size: 18px;
  color: ${theme.colors.text};
  margin-bottom: 20px;
  line-height: 1.3;
`;

const PlanCardIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.radius.md};
  background: rgba(255, 107, 107, 0.1);
  color: ${theme.colors.primary};
  font-size: 20px;
  flex-shrink: 0;
`;

const PlanPriceRow = styled.div`
  margin-bottom: 4px;
`;

const PlanPriceAmount = styled.span`
  font-size: 42px;
  font-weight: 800;
  color: ${theme.colors.text};
  letter-spacing: -0.04em;
  line-height: 1;
`;

const PlanPricePeriod = styled.span`
  font-size: 16px;
  color: ${theme.colors.textMuted};
  font-weight: 500;
  margin-left: 4px;
`;

const PlanPriceNote = styled.div`
  color: ${theme.colors.textMuted};
  font-size: 13px;
  margin-bottom: 22px;
  font-weight: 500;
`;

const PlanFeatureList = styled.ul`
  list-style: none;
  margin: 0 0 24px;
  padding: 0;
`;

const PlanFeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 14px;
  color: ${theme.colors.textSecondary};
  line-height: 1.45;

  &::before {
    content: '✓';
    color: ${theme.colors.primary};
    font-weight: 800;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const TrialBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  background: ${theme.colors.accentGradient};
  color: white;
  padding: 6px 12px;
  border-radius: ${theme.radius.pill};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
`;

const CurrentPlanBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
  padding: 6px 12px;
  border-radius: ${theme.radius.pill};
  background: rgba(255, 107, 107, 0.12);
  color: ${theme.colors.primary};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const PeriodToggleWrapper = styled.div`
  background: ${theme.colors.card};
  border-radius: ${theme.radius.pill};
  box-shadow: ${theme.shadows.sm};
  border: 1px solid ${theme.colors.cardBorder};
  padding: 6px 8px;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin: 0 auto 28px auto;
`;

const PeriodLabel = styled.span<{ $active: boolean }>`
  font-weight: 600;
  font-size: 15px;
  color: ${props => (props.$active ? theme.colors.text : theme.colors.textMuted)};
  transition: color 0.2s;
  padding: 0 8px;
`;

const PlanSelectButton = styled.button`
  background: ${theme.colors.accentGradient};
  color: #fff;
  border: none;
  border-radius: ${theme.radius.pill};
  padding: 14px 0;
  font-weight: 700;
  font-size: 15px;
  width: 100%;
  margin-top: 4px;
  cursor: pointer;
  transition: all 0.25s ease;
  letter-spacing: -0.01em;
  box-shadow: 0 4px 14px rgba(255, 107, 107, 0.35);

  &:hover:not(:disabled) {
    box-shadow: ${theme.shadows.glow};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PlansCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 24px;
  background: ${theme.colors.card};
  border: 1px solid ${theme.colors.cardBorder};
  color: ${theme.colors.textMuted};
  font-size: 22px;
  cursor: pointer;
  font-weight: 400;
  z-index: 10;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  line-height: 1;

  &:hover {
    color: ${theme.colors.primary};
    border-color: rgba(255, 107, 107, 0.4);
    background: rgba(255, 107, 107, 0.06);
  }
`;

const PeriodToggleTrack = styled.span<{ $yearly: boolean }>`
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: ${props => (props.$yearly ? theme.colors.accentGradient : '#e2e8f0')};
  border-radius: ${theme.radius.pill};
  transition: background 0.25s ease;
`;

const PeriodToggleKnob = styled.span<{ $yearly: boolean }>`
  position: absolute;
  left: ${props => (props.$yearly ? '24px' : '2px')};
  top: 2px;
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.15);
  transition: left 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
`;

const PeriodToggleLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
  margin: 0 4px;
  vertical-align: middle;
`;

const RequiredAsterisk = styled.span`
  color: #ff4646;
  margin-left: 2px;
  font-size: 1em;
  display: inline-block;
`;

const InfoIconButton = styled.button`
  background: none;
  border: none;
  padding: 0 4px;
  margin-left: 6px;
  color: #ff6b6b;
  cursor: pointer;
  font-size: 1em;
  display: inline-flex;
  align-items: center;
  transition: color 0.2s;
  &:hover {
    color: #ff8e53;
  }

  @media (max-width: 480px) {
    margin-left: 4px;
  }
`;

const ModalFooter = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #edf2f7;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    margin-top: 20px;
    padding-top: 12px;
  }
`;

const MobileLogo = styled.div`
  width: 120px;
  height: auto;
  margin-left: 16px;
`;

interface Location {
  id: string;
  locationId: string;
  locationName: string;
  subscriptionDate: string;
  dueDate: string;
  status: string;
  planName: string;
  razorpaySubscriptionId: string;
  whatsappConnected?: boolean;
  isTrialActive?: boolean;
  trialEndDate?: string;
  actualDueDate?: string;
}

const applyPendingWhatsAppFlags = (items: Location[]): Location[] =>
  items.map((loc) => ({
    ...loc,
    whatsappConnected:
      loc.whatsappConnected ||
      sessionStorage.getItem(`wa-connected-${loc.locationId}`) === '1',
  }));

const isLocationRowCancelled = (status?: string) =>
  ['cancelled', 'terminated'].includes(String(status || '').toLowerCase());

const mapSubscriptionsToLocations = (subscriptions: any[]): Location[] => {
  const rows = applyPendingWhatsAppFlags(
    subscriptions.flatMap((subscription) =>
      (subscription.subAccounts || []).map((subAccount: any) => {
        const subscriptionCreatedDate = new Date(subscription.subscriptionCreatedDate);
        const subscriptionEndDate = new Date(subscription.subscriptionEndDate);
        const trialEndDate = new Date(subscriptionCreatedDate);
        trialEndDate.setDate(trialEndDate.getDate() + 14);
        const now = new Date();
        const isTrialActive =
          now < trialEndDate && subscription.status.toLowerCase() === 'authenticated';
        const dueDate = isTrialActive
          ? trialEndDate.toLocaleDateString()
          : subscriptionEndDate.toLocaleDateString();

        const parentStatus = String(subscription.status || '').toLowerCase();
        const rowStatus =
          subAccount.accessStatus === 'cancelled' ||
          ['cancelled', 'terminated'].includes(parentStatus)
            ? 'cancelled'
            : subscription.status;

        return {
          id: subAccount._id || subAccount.locationId,
          locationId: subAccount.locationId,
          locationName: subAccount.locationName,
          subscriptionDate: subscriptionCreatedDate.toLocaleDateString(),
          dueDate,
          status: rowStatus,
          planName: subscription.planName,
          razorpaySubscriptionId: subscription.razorpaySubscriptionId,
          whatsappConnected: !!subAccount.whatsappConnected,
          isTrialActive,
          trialEndDate: trialEndDate.toLocaleDateString(),
          actualDueDate: subscriptionEndDate.toLocaleDateString(),
        };
      })
    )
  );

  const byLocationId = new Map<string, Location>();
  for (const row of rows) {
    const existing = byLocationId.get(row.locationId);
    if (!existing) {
      byLocationId.set(row.locationId, row);
      continue;
    }
    const rowCancelled = isLocationRowCancelled(row.status);
    const existingCancelled = isLocationRowCancelled(existing.status);
    if (!rowCancelled && existingCancelled) {
      byLocationId.set(row.locationId, row);
    }
  }
  return Array.from(byLocationId.values());
};

interface ExtendedPlan extends Plan {
  isTrial?: boolean;
  trialDays?: number;
}

type PaymentResponseData = {
  subscriptionId?: string;
  orderId?: string;
  amount?: number;
  currency?: string;
  status?: string;
  [key: string]: any;
};

declare global {
  interface Window {
    Razorpay: any;
  }
}

const SubscribedLocations: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState('10');
  const [searchTerm, setSearchTerm] = useState('');
  const [locations, setLocations] = useState<Location[]>([]);
  const [rawSubscriptions, setRawSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddLocationLoading, setIsAddLocationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<Location | null>(null);
  const [whatsappDisconnectOpen, setWhatsappDisconnectOpen] = useState(false);
  const [locationToDisconnect, setLocationToDisconnect] = useState<Location | null>(null);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const navigate = useNavigate();
  const location = useLocation();

  const {
    showLogoutConfirm,
    handleLogoutClick,
    handleLogoutConfirm,
    handleLogoutCancel
  } = useLogoutHandler();

  const [addLocationId, setAddLocationId] = useState('');
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const [isPlansModalOpen, setIsPlansModalOpen] = useState(false);
  const [showLocationIdInfo, setShowLocationIdInfo] = useState(false);
  const [plansError, setPlansError] = useState('');
  const [subscriptionError, setSubscriptionError] = useState('');
  const [isUpgradeMode, setIsUpgradeMode] = useState(false);

  const [plans, setPlans] = useState<ExtendedPlan[]>([]);
  const [isPlansLoading, setIsPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [planPeriod, setPlanPeriod] = useState<'Monthly' | 'Yearly'>('Monthly');
  const resubscribeTargetRef = useRef<Location | null>(null);
  const [resubscribeTarget, setResubscribeTarget] = useState<Location | null>(null);

  // Fixed: Add state for 11+ plans with proper logic
  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [isActivatingTestSub, setIsActivatingTestSub] = useState(false);
  const [plansDemoMode, setPlansDemoMode] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  const fetchLocations = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getSubscribedLocations();
      setRawSubscriptions(response.subscriptions);
      setLocations(mapSubscriptionsToLocations(response.subscriptions));
      response.subscriptions.forEach((sub: any) => {
        (sub.subAccounts || []).forEach((sa: any) => {
          if (sa.whatsappConnected && sa.locationId) {
            sessionStorage.setItem('wa-connected-' + sa.locationId, '1');
          }
        });
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred while fetching locations';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const completeResubscribeFlow = useCallback(async (razorpaySubscriptionId?: string) => {
    const target = resubscribeTargetRef.current;
    if (!target?.locationId) {
      return false;
    }

    try {
      await relinkSubAccountLocation(target.locationId, razorpaySubscriptionId);
      toast.success(`${target.locationName || 'Sub-account'} restored on your plan`);
      resubscribeTargetRef.current = null;
      setResubscribeTarget(null);
      setIsPlansModalOpen(false);
      await fetchLocations();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to restore sub-account';
      toast.error(message);
      setSubscriptionError(message);
      return false;
    }
  }, [fetchLocations]);

  const handleResubscribe = async (location: Location) => {
    resubscribeTargetRef.current = location;
    setResubscribeTarget(location);
    setSubscriptionError('');
    setIsUpgradeMode(false);
    setIsPlansModalOpen(true);
    setPlans([]);
    setPlansError('');
    setIsPlansLoading(true);

    try {
      const response = await getSubscriptionPlans();
      setPlansDemoMode(response.demoMode === true);
      setPlans(filterSubscriptionPlans(response.data || []) as ExtendedPlan[]);
    } catch (error: any) {
      setPlansError(error.message || 'Failed to load plans');
    } finally {
      setIsPlansLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations, location.key]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchLocations();
      }
    };

    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);
    return () => {
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [fetchLocations]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = user.name || '';
  const DEMO_EMAILS = ['zeel.prajapati.tot@gmail.com', 'dev.patel@theonetechnologies.co.in'];
  const isDemoUser =
    user.isDemoUser === true ||
    DEMO_EMAILS.includes(String(user.email || '').trim().toLowerCase());

  const handleLogout = () => {
    handleLogoutClick();
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const filteredLocations = locations
    .filter(location =>
      Object.values(location).some(value =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    )
    .sort((a, b) => {
      if (!sortColumn) return 0;
      let aValue = a[sortColumn as keyof Location];
      let bValue = b[sortColumn as keyof Location];

      // Handle undefined values
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return sortDirection === 'asc' ? -1 : 1;
      if (bValue === undefined) return sortDirection === 'asc' ? 1 : -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      if (aValue === bValue) return 0;
      if (sortDirection === 'asc') {
        return (aValue as any) > (bValue as any) ? 1 : -1;
      } else {
        return (aValue as any) < (bValue as any) ? 1 : -1;
      }
    });

  const handleOpenCancelModal = (location: Location) => {
    setSelectedSubscription(location);
    setCancelError(null);
    setCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setCancelModalOpen(false);
    setSelectedSubscription(null);
    setCancelError(null);
  };

  const handleCancelSubmit = async () => {
    if (!selectedSubscription) return;
    setCancelLoading(true);
    setCancelError(null);
    try {
      await cancelSubscription(
        selectedSubscription.razorpaySubscriptionId,
        false,
        selectedSubscription.locationId
      );
      setCancelModalOpen(false);
      const response = await getSubscribedLocations();
      setRawSubscriptions(response.subscriptions);
      setLocations(mapSubscriptionsToLocations(response.subscriptions));
    } catch (err: any) {
      setCancelError(err.message || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const getSortArrow = (column: string) => {
    if (sortColumn === column) {
      return sortDirection === 'asc' ? '▲' : '▼';
    }
    return '△';
  };

  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = resolve;
        document.body.appendChild(script);
      });
    };
    loadRazorpayScript();
  }, []);

  const openRazorpay = ({
    orderId,
    amount,
    currency,
    subscriptionId
  }: {
    orderId?: string;
    amount?: number;
    currency?: string;
    subscriptionId?: string;
  }) => {
    if (!orderId && !subscriptionId) {
      console.error("❌ Neither Order ID nor Subscription ID is present! Cannot open Razorpay.");
      return;
    }

    const options: any = {
      key: process.env.REACT_APP_RAZORPAY_KEY || "rzp_live_YVFcxUEMhDDFp9",
      name: "The One Technologies",
      description: orderId ? "Dynamic Sub-Account Payment" : "Subscription Payment",
      prefill: {
        name: user?.name || "User",
        email: user?.email || "user@example.com",
      },
      theme: { color: "#F37254" },
      handler: async function (response: any) {
        console.log('Payment successful:', response);
        toast.success("Payment Successful!");
        if (resubscribeTargetRef.current) {
          await completeResubscribeFlow(subscriptionId);
          return;
        }
        navigate(APP_ROUTES.DASHBOARD);
      },
      modal: {
        ondismiss: async function () {
          console.log('Payment modal dismissed');
          toast.error('Payment not completed. Please finish payment before adding a location.');
          if (!subscriptionId) return;
          try {
            await cancelPendingSubscription(subscriptionId);
            const refreshed = await getSubscribedLocations();
            setRawSubscriptions(refreshed.subscriptions);
          } catch (err) {
            console.warn('Failed to cancel pending subscription:', err);
          }
        }
      },
      notes: {}
    };

    if (orderId) {
      if (typeof amount !== 'number' || !currency) {
        toast.error('Payment information missing. Please try again.');
        return;
      }
      options.order_id = orderId;
      options.amount = amount * 100;
      options.currency = currency;
      options.notes.order_id = orderId;
    } else if (subscriptionId) {
      options.subscription_id = subscriptionId;
      options.notes.subscription_id = subscriptionId;
    }

    try {
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Error opening Razorpay:', error);
      toast.error('Failed to open payment window');
    }
  };

  // Fixed: Improved subscription state detection
  const getUserSubscriptionState = (locations: Location[]) => {
    // Check if user has any active subscriptions without sub-accounts
    const hasValidSubscriptionWithoutSubaccounts = rawSubscriptions.some(subscription =>
      subscription.subAccounts.length === 0 &&
      isPaidSubscriptionStatus(subscription.status)
    );

    if (hasValidSubscriptionWithoutSubaccounts) {
      return 'ACTIVE';
    }

    if (locations.length === 0) {
      return 'FREE_TRIAL';
    }

    const hasPaidSubscription = locations.some(loc => isPaidSubscriptionStatus(loc.status));

    if (hasPaidSubscription) {
      return 'ACTIVE';
    }

    return 'FREE_TRIAL';
  };

  const paidSubscriptions = getPaidSubscriptions(rawSubscriptions, isPaidSubscriptionStatus);

  const resolveSubAccountCount = (planName: string): number | undefined => {
    if (!planName.includes('11+ Sub-accounts')) return undefined;
    const hasExisting11Plus = rawSubscriptions.some(
      sub =>
        sub.planName?.includes('11+ Sub-accounts') &&
        isPaidSubscriptionStatus(sub.status)
    );
    return hasExisting11Plus ? 1 : 11;
  };

  const isCurrentPlanCard = (plan: ExtendedPlan) =>
    isPlanCardCurrent(plan, rawSubscriptions, isPaidSubscriptionStatus);

  const finishRestoreFromPayload = async (payload: Record<string, any>, locationName?: string) => {
    if (payload.needsPayment) {
      return 'needsPayment' as const;
    }

    if (payload.restored === true) {
      if (Array.isArray(payload.subscriptions)) {
        setRawSubscriptions(payload.subscriptions);
        setLocations(mapSubscriptionsToLocations(payload.subscriptions));
      } else {
        await fetchLocations();
      }

      toast.success(`${locationName || 'Sub-account'} restored successfully`);
      resubscribeTargetRef.current = null;
      setResubscribeTarget(null);
      setIsPlansModalOpen(false);
      return 'done' as const;
    }

    if (payload.subscriptionId && resubscribeTargetRef.current) {
      const relinked = await completeResubscribeFlow(payload.subscriptionId);
      return relinked ? ('done' as const) : ('failed' as const);
    }

    throw new Error('Restore did not complete. Deploy the latest backend and try again.');
  };

  // Fixed: Improved plan selection logic
  const handleSelectPlan = async (planId: string, planName: string) => {
    const planCard = plans.find(p => p.id === planId);
    const isResubscribeMode = !!resubscribeTarget;
    if (planCard && isCurrentPlanCard(planCard) && !isResubscribeMode) return;

    setSelectedPlanId(planId);
    setSelectedPlanName(planName);
    setSubscriptionError('');

    if (isResubscribeMode && resubscribeTarget?.locationId) {
      setIsLoadingAction(true);
      const subAccountCount = resolveSubAccountCount(planName);
      const subscriptionState = getUserSubscriptionState(locations);

      try {
        let response: { data?: Record<string, any> };

        try {
          response = await restoreSubAccountLocation(
            resubscribeTarget.locationId,
            planId
          );
        } catch (primaryError) {
          if (primaryError instanceof ApiEndpointUnavailableError) {
            response = await restoreSubAccountViaCreate(
              resubscribeTarget.locationId,
              planId,
              {
                payNow: subscriptionState !== 'FREE_TRIAL',
                subAccountCount,
              }
            );
          } else {
            throw primaryError;
          }
        }

        const payload = response.data || {};
        const result = await finishRestoreFromPayload(
          payload,
          resubscribeTarget.locationName
        );

        if (result === 'needsPayment') {
          await handleCreateSubscription(planId, planName, subAccountCount);
        }
      } catch (error: any) {
        const message = error?.message || 'Failed to restore sub-account';
        setSubscriptionError(message);
        toast.error(message);
      } finally {
        setIsLoadingAction(false);
      }
      return;
    }

    const subAccountCount = resolveSubAccountCount(planName);
    await handleCreateSubscription(planId, planName, subAccountCount);
  };

  // Fixed: Separate function for subscription creation
  const handleCreateSubscription = async (
    planId: string,
    planName: string,
    subAccountCount?: number
  ) => {
    setIsLoadingAction(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");

      const subscriptionState = getUserSubscriptionState(locations);

      // Validate 11+ plan logic (first purchase = 11 sub-accounts, no UI input needed)
      if (planName.includes('11+ Sub-accounts') && subAccountCount != null) {
        const isFirst11Plus = subAccountCount === 11;
        if (isFirst11Plus && subAccountCount !== 11) {
          toast.error('First 11+ plan must be for exactly 11 sub-accounts.');
          setIsLoadingAction(false);
          return;
        }
        if (!isFirst11Plus && subAccountCount < 1) {
          toast.error('Please enter a valid sub-account count.');
          setIsLoadingAction(false);
          return;
        }
      }

      // Compose subscription data
      const subscriptionData: any = {
        planId,
        payNow: subscriptionState === 'FREE_TRIAL' ? false : true,
      };

      if (resubscribeTargetRef.current?.locationId) {
        subscriptionData.restoreLocationId = resubscribeTargetRef.current.locationId;
      }

      // Add subAccountCount only for 11+ plans
      if (planName.includes('11+ Sub-accounts') && subAccountCount != null) {
        subscriptionData.subAccountCount = subAccountCount;
      }

      console.log('Creating subscription with data:', subscriptionData);

      const response = await createSubscription(subscriptionData);
      const data: PaymentResponseData = response.data || {};

      if (response.success && data) {
        setSubscriptionError('');

        const isActivated =
          data.status === 'active' || data.status === 'authenticated';

        if (data.restored === true) {
          await finishRestoreFromPayload(data, resubscribeTargetRef.current?.locationName);
          return;
        }

        // Demo / no-checkout: subscription ready — skip Razorpay
        if (isActivated && data.subscriptionId) {
          toast.success(
            resubscribeTargetRef.current
              ? 'Subscription ready — restoring sub-account…'
              : 'Test subscription activated successfully!'
          );
          if (resubscribeTargetRef.current) {
            await completeResubscribeFlow(data.subscriptionId);
            return;
          }
          setIsPlansModalOpen(false);
          await fetchLocations();
          return;
        }

        if (data.orderId) {
          openRazorpay({
            orderId: data.orderId,
            amount: data.amount,
            currency: data.currency,
            subscriptionId: data.subscriptionId
          });
        } else if (data.subscriptionId) {
          openRazorpay({ subscriptionId: data.subscriptionId });
        } else {
          throw new Error('No order ID or subscription ID received from server');
        }
      } else {
        throw new Error('No order ID or subscription ID received from server');
      }
    } catch (error: any) {
      console.error("Error in subscription flow:", error);
      // Extract error message properly from the error object
      let errorMessage = error?.message || error?.response?.message || "Failed to create subscription";

      // Make error message more user-friendly
      if (errorMessage.includes("You can still add sub-accounts to your existing 11+ subscription")) {
        errorMessage = "You already have an 11+ subscription. You can add more sub-accounts to your existing subscription instead of creating a new one.";
      }

      setSubscriptionError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleBuySubscription = async () => {
    resubscribeTargetRef.current = null;
    setResubscribeTarget(null);
    const subscriptionState = getUserSubscriptionState(locations);
    setIsUpgradeMode(subscriptionState === 'ACTIVE');
    setIsPlansModalOpen(true);
    setPlans([]);
    setPlansError('');
    setSubscriptionError('');
    setIsPlansLoading(true);

    try {
      const response = await getSubscriptionPlans();
      setPlansDemoMode(response.demoMode === true);
      const fetchedPlans = response.data || [];
      let filteredPlans = filterSubscriptionPlans(fetchedPlans);

      // Add trial information to plans if user is in free trial
      if (subscriptionState === 'FREE_TRIAL') {
        filteredPlans = filteredPlans.map(plan => ({
          ...plan,
          isTrial: true,
          trialDays: 14
        })) as ExtendedPlan[];
      }

      setPlans(filteredPlans as ExtendedPlan[]);
    } catch (error: any) {
      setPlansError(error.message || 'Failed to load plans');
    } finally {
      setIsPlansLoading(false);
    }
  };

  const handleUpgradePlan = async () => {
    resubscribeTargetRef.current = null;
    setResubscribeTarget(null);
    setIsUpgradeMode(true);
    setIsPlansModalOpen(true);
    setPlans([]);
    setPlansError('');
    setIsPlansLoading(true);

    try {
      const response = await getSubscriptionPlans();
      setPlansDemoMode(response.demoMode === true);
      const fetchedPlans = response.data || [];
      const filteredPlans = filterSubscriptionPlans(fetchedPlans);
      setPlans(filteredPlans as ExtendedPlan[]);
    } catch (error: any) {
      setPlansError(error.message || 'Failed to load plans');
    } finally {
      setIsPlansLoading(false);
    }
  };



  const handleActivateTestSubscription = async () => {
    setIsActivatingTestSub(true);
    try {
      await createTestSubscription();
      toast.success('Test subscription activated! You can now add a location.');
      // Refresh the locations list
      const response = await getSubscribedLocations();
      setRawSubscriptions(response.subscriptions);
      setLocations(mapSubscriptionsToLocations(response.subscriptions));
    } catch (err: any) {
      toast.error(err.message || 'Failed to activate test subscription');
    } finally {
      setIsActivatingTestSub(false);
    }
  };

  const handleAddLocationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setIsAddLocationLoading(true);

    try {
      if (!addLocationId.trim()) {
        setFormError('Oops! You forgot to add location ID.');
        setIsAddLocationLoading(false);
        return;
      }

      if (!isDemoUser && locationAddBlockMessage) {
        setFormError(locationAddBlockMessage);
        toast.error(locationAddBlockMessage);
        setIsAddLocationLoading(false);
        return;
      }

      const response = await initiateGHLAuth(addLocationId);

      localStorage.setItem('tempLocationId', addLocationId);
      localStorage.setItem('ghlRedirectUrl', response.data);

      window.location.href = response.data;
    } catch (error: any) {
      setFormError(error.message || 'Failed to authenticate with GHL');
    } finally {
      setIsAddLocationLoading(false);
    }
  };

  const handleAddLocationCancel = () => {
    setAddLocationId("");
    setFormError("");
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const pendingSubscription = rawSubscriptions.find(
    sub => !isDemoUser && isPendingSubscriptionStatus(sub.status)
  );

  const paidSubscriptionsForLocation = rawSubscriptions.filter(sub => {
    if (!isPaidSubscriptionStatus(sub.status)) return false;
    if (!isDemoUser && isTestSubscription(sub)) return false;
    return true;
  });

  const canAddLocation = paidSubscriptionsForLocation.some(sub => {
    if (sub.planName?.includes('Single Sub-account')) {
      return !sub.subAccounts?.length;
    }
    if (sub.planName?.includes('11+ Sub-accounts')) {
      return (sub.subAccounts?.length || 0) < 11;
    }
    return true;
  });

  const locationAddBlockMessage = !isDemoUser
    ? pendingSubscription
      ? 'Please complete your Razorpay payment before adding a location ID.'
      : !canAddLocation
        ? 'Please purchase and complete payment for a subscription before adding a location ID.'
        : null
    : null;

  const handleResumePendingPayment = () => {
    if (!pendingSubscription?.razorpaySubscriptionId) return;
    openRazorpay({ subscriptionId: pendingSubscription.razorpaySubscriptionId });
  };

  const handleOpenWhatsAppDisconnect = (location: Location) => {
    setLocationToDisconnect(location);
    setWhatsappDisconnectOpen(true);
  };

  const handleConfirmWhatsAppDisconnect = async () => {
    if (!locationToDisconnect) return;
    setDisconnectLoading(true);
    try {
      await disconnectWhatsApp(locationToDisconnect.locationId);
      sessionStorage.removeItem(`wa-connected-${locationToDisconnect.locationId}`);
      toast.success('WhatsApp disconnected successfully');
      setWhatsappDisconnectOpen(false);
      setLocationToDisconnect(null);
      await fetchLocations();
    } catch (err: any) {
      toast.error(err.message || 'Failed to disconnect WhatsApp');
    } finally {
      setDisconnectLoading(false);
    }
  };

  return (
    <DashboardContainer>
      <Sidebar
        isCollapsed={isCollapsed}
        isMobileOpen={isMobileOpen}
        onMobileClose={toggleMobileMenu}
      />

      <MainContent isCollapsed={isCollapsed} isMobileOpen={isMobileOpen}>
        <TopBar isCollapsed={isCollapsed} isMobileOpen={isMobileOpen}>
          <ToggleButton onClick={() => {
            if (window.innerWidth <= 768) {
              toggleMobileMenu();
            } else {
              setIsCollapsed(!isCollapsed);
            }
          }}>
            <FontAwesomeIcon icon={faBars} />
          </ToggleButton>
          <UserProfile ref={dropdownRef} onClick={() => setIsProfileOpen(!isProfileOpen)}>
            <UserInfo>
              <UserName>{userName}</UserName>
            </UserInfo>
            <UserAvatar>{userName.charAt(0)}</UserAvatar>
            <UserDropdown isOpen={isProfileOpen}>
              <DropdownItem onClick={() => navigate('/profile')}>
                <FontAwesomeIcon icon={faUser} />
                Profile
              </DropdownItem>
              <DropdownItem onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                Logout
              </DropdownItem>
            </UserDropdown>
          </UserProfile>
        </TopBar>

        <FormContainer>
          {!isPlansModalOpen ? (
            <form onSubmit={handleAddLocationSubmit} noValidate>
              {pendingSubscription && (
                <PaymentNotice $variant="warning">
                  <strong>Payment required</strong>
                  You started checkout for <em>{pendingSubscription.planName}</em> but payment is not complete.
                  Please finish payment in Razorpay before adding a location.
                  <div style={{ marginTop: 12 }}>
                    <ActionButton
                      type="button"
                      variant="primary"
                      onClick={handleResumePendingPayment}
                    >
                      Complete Payment
                    </ActionButton>
                  </div>
                </PaymentNotice>
              )}
              <FormGroup>
                <Label htmlFor="locationId">
                  Location ID
                  <RequiredAsterisk>*</RequiredAsterisk>
                  <InfoIconButton
                    type="button"
                    title="How to find Location ID"
                    onClick={() => setShowLocationIdInfo(v => !v)}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </InfoIconButton>
                </Label>
                <Input
                  id="locationId"
                  type="text"
                  value={addLocationId}
                  onChange={(e) => {
                    setAddLocationId(e.target.value);
                    if (formError) setFormError('');
                  }}
                  placeholder="Enter location ID"
                  aria-required="true"
                  aria-invalid={!!locationAddBlockMessage || !!formError}
                  hasError={!!locationAddBlockMessage || !!formError}
                />
                {showLocationIdInfo && (
                  <InfoText style={{ color: '#ff6b6b', fontWeight: 400 }}>
                    To get a location ID from a GoHighLevel (GHL) account, navigate to the location view, then click "Settings" and then "Business Info". The location ID will be displayed in this section.
                  </InfoText>
                )}
                {locationAddBlockMessage && (
                  <ErrorText role="alert">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {locationAddBlockMessage}
                  </ErrorText>
                )}
                {formError && formError !== locationAddBlockMessage && (
                  <ErrorText role="alert">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {formError}
                  </ErrorText>
                )}

              </FormGroup>

              <ActionButtonGroup>
                <ActionButton
                  type="submit"
                  variant="primary"
                  disabled={isAddLocationLoading || !!locationAddBlockMessage}
                >
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  {isAddLocationLoading ? 'Processing...' : 'Add Location'}
                </ActionButton>
                <ActionButton
                  type="reset"
                  variant="primary"
                  onClick={handleAddLocationCancel}
                >
                  Cancel
                </ActionButton>
                <ActionButton
                  type="button"
                  variant="primary"
                  onClick={handleBuySubscription}
                >
                  {getUserSubscriptionState(locations) === 'ACTIVE' ? 'Upgrade Plan' : 'Buy Subscription'}
                </ActionButton>
              </ActionButtonGroup>
            </form>
          ) : (
            <PlansModalOuter>
              <PlansModalInner>
                <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <PeriodToggleWrapper>
                    <PeriodLabel $active={planPeriod === 'Monthly'}>Monthly</PeriodLabel>
                    <PeriodToggleLabel>
                      <input
                        type="checkbox"
                        checked={planPeriod === 'Yearly'}
                        onChange={() => setPlanPeriod(planPeriod === 'Monthly' ? 'Yearly' : 'Monthly')}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <PeriodToggleTrack $yearly={planPeriod === 'Yearly'} />
                      <PeriodToggleKnob $yearly={planPeriod === 'Yearly'} />
                    </PeriodToggleLabel>
                    <PeriodLabel $active={planPeriod === 'Yearly'}>Yearly</PeriodLabel>
                  </PeriodToggleWrapper>
                </div>
                <PlansCloseButton
                  onClick={() => {
                    resubscribeTargetRef.current = null;
                    setResubscribeTarget(null);
                    setIsPlansModalOpen(false);
                    setSubscriptionError('');
                  }}
                >
                  ×
                </PlansCloseButton>
                {isPlansLoading ? (
                  <Loader />
                ) : plansError ? (
                  <p style={{ color: 'red' }}>{plansError}</p>
                ) : subscriptionError ? (
                  <div style={{
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px',
                    color: '#dc2626',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {subscriptionError}
                      </div>
                      <button
                        onClick={() => setSubscriptionError('')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: '16px',
                          padding: '4px',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Clear error"
                      >
                        ×
                      </button>
                    </div>
                    {subscriptionError.includes("You already have an 11+ subscription") && (
                      <div style={{
                        background: '#f0f9ff',
                        border: '1px solid #bae6fd',
                        borderRadius: '6px',
                        padding: '12px',
                        marginTop: '8px',
                        color: '#0369a1',
                        fontSize: '13px'
                      }}>
                        <strong>💡 Suggestion:</strong> You can add more locations to your existing subscription using the "Add Location".
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <PlansSectionHeader>
                      <PlansSectionTitle>
                        Choose your <span>plan</span>
                      </PlansSectionTitle>
                      <PlansSectionSubtitle>
                        {resubscribeTarget
                          ? `Restore "${resubscribeTarget.locationName}" — choose a plan to re-activate this sub-account`
                          : planPeriod === 'Yearly'
                            ? 'Save with annual billing — per sub-account pricing'
                            : 'Flexible monthly billing — per sub-account pricing'}
                      </PlansSectionSubtitle>
                      {(plansDemoMode || isDemoUser) && (
                        <DemoPlansNotice>
                          Demo account — prices shown for reference only. No Razorpay charge applies.
                        </DemoPlansNotice>
                      )}
                      {paidSubscriptions.length > 0 && !resubscribeTarget && (
                        <DemoPlansNotice style={{ color: theme.colors.textSecondary, fontWeight: 500 }}>
                          You are currently on{' '}
                          <strong style={{ color: theme.colors.text }}>
                            {String(paidSubscriptions[0].planName || 'your plan')}
                          </strong>
                          {paidSubscriptions.length > 1 ? ` (+${paidSubscriptions.length - 1} more)` : ''}.
                          Select a different plan below to upgrade.
                        </DemoPlansNotice>
                      )}
                    </PlansSectionHeader>
                    <PlansList>
                      {plans
                        .filter(plan => planMatchesPeriod(plan, planPeriod))
                        .sort((a, b) => {
                          const isASinglePlan = isSingleSubAccountPlan(a.item.name);
                          const isBSinglePlan = isSingleSubAccountPlan(b.item.name);
                          if (isASinglePlan && !isBSinglePlan) return 1;
                          if (!isASinglePlan && isBSinglePlan) return -1;
                          return 0;
                        })
                        .map((plan) => {
                          const isSinglePlan = isSingleSubAccountPlan(plan.item.name);
                          const subscriptionState = getUserSubscriptionState(locations);
                          const displayAmount = getPlanDisplayAmount(plan);
                          const isMonthly = plan.item.name.includes('Monthly');
                          const isResubscribeMode = !!resubscribeTarget;
                          const isUsersCurrentPlan =
                            !isResubscribeMode && isCurrentPlanCard(plan);

                          return (
                            <PlanItem
                              key={plan.id}
                              $highlight={!isSinglePlan && !isUsersCurrentPlan}
                              $isCurrent={isUsersCurrentPlan}
                            >
                              {plan.isTrial && !isUsersCurrentPlan && !isResubscribeMode && (
                                <TrialBadge>14 Days Free Trial</TrialBadge>
                              )}
                              {isUsersCurrentPlan && (
                                <CurrentPlanBadge>Your current plan</CurrentPlanBadge>
                              )}
                              <PlanCardHeader>
                                <PlanCardIcon>
                                  <FontAwesomeIcon icon={faWhatsapp} />
                                </PlanCardIcon>
                                {plan.item.name}
                              </PlanCardHeader>
                              <PlanPriceRow>
                                <PlanPriceAmount>
                                  {formatPlanPrice(displayAmount)}
                                </PlanPriceAmount>
                                <PlanPricePeriod>/{isMonthly ? 'month' : 'year'}</PlanPricePeriod>
                              </PlanPriceRow>
                              <PlanPriceNote>
                                Per Sub-Account
                                {(plansDemoMode || isDemoUser) && ' · No charge in demo'}
                              </PlanPriceNote>
                              <PlanFeatureList>
                                {PLAN_FEATURE_LIST.map((f, i) => (
                                  <PlanFeatureItem key={i}>{f}</PlanFeatureItem>
                                ))}
                              </PlanFeatureList>

                              <PlanSelectButton
                                disabled={isUsersCurrentPlan || isLoadingAction}
                                onClick={() => handleSelectPlan(plan.id, plan.item.name)}
                              >
                                {isResubscribeMode
                                  ? 'Restore with this plan'
                                  : isUsersCurrentPlan
                                    ? 'Current Plan'
                                    : (() => {
                                        switch (subscriptionState) {
                                          case 'FREE_TRIAL':
                                            return 'Start 14 Days Free Trial';
                                          case 'ACTIVE':
                                            return 'Upgrade Plan';
                                          default:
                                            return 'Select Plan';
                                        }
                                      })()}
                              </PlanSelectButton>
                            </PlanItem>
                          );
                        })}
                    </PlansList>
                  </>
                )}
              </PlansModalInner>
            </PlansModalOuter>
          )}
        </FormContainer>

        {!isPlansModalOpen && (
        <PageContainer>
          <TableHeader>
            <TableTitle>Subscribed List</TableTitle>
            {locations.length > 0 && (
              <TableControls>
                <EntriesControl>
                  <span>Show</span>
                  <Select
                    value={entriesPerPage}
                    onChange={(e) => setEntriesPerPage(e.target.value)}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </Select>
                  <span>entries</span>
                </EntriesControl>
                <SearchControl>
                  <span>Search:</span>
                  <SearchInputWrapper>
                    <SearchInput
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search..."
                    />
                    {searchTerm && (
                      <ClearButton type="button" onClick={() => setSearchTerm('')} aria-label="Clear search">
                        ×
                      </ClearButton>
                    )}
                  </SearchInputWrapper>
                </SearchControl>
              </TableControls>
            )}
          </TableHeader>

          {isLoading ? (
            <Loader />
          ) : error && locations.length === 0 ? (
            <ErrorImageWrapper>
              <EmptyImage src={noDataFound} alt="Error loading data" />
            </ErrorImageWrapper>
          ) : filteredLocations.length === 0 ? (
            <EmptyState>
              <EmptyStateImage src={searchTerm ? noResults : noDataFound} alt="No data found" />
              {rawSubscriptions.length === 0 && isDemoUser && (
                <div style={{
                  marginTop: '24px',
                  padding: '20px 28px',
                  background: '#fff8e1',
                  border: '1.5px dashed #ffb300',
                  borderRadius: '12px',
                  textAlign: 'center',
                  maxWidth: '420px',
                  margin: '24px auto 0'
                }}>
                  <div style={{ fontSize: '15px', color: '#856404', fontWeight: 600, marginBottom: '8px' }}>
                    🧪 Testing Mode
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
                    No subscription found. Activate a free test subscription to proceed without payment.
                  </div>
                  <button
                    onClick={handleActivateTestSubscription}
                    disabled={isActivatingTestSub}
                    style={{
                      background: 'linear-gradient(135deg, #ff6b6b, #ff8e53)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: isActivatingTestSub ? 'not-allowed' : 'pointer',
                      opacity: isActivatingTestSub ? 0.7 : 1,
                    }}
                  >
                    {isActivatingTestSub ? 'Activating...' : 'Activate Test Subscription'}
                  </button>
                </div>
              )}
            </EmptyState>
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    <Th>Location ID</Th>
                    <Th onClick={() => handleSort('locationName')} style={{ cursor: 'pointer' }}>
                      Location Name <span style={{ color: sortColumn === 'locationName' ? '#333' : '#bbb' }}>{getSortArrow('locationName')}</span>
                    </Th>
                    <Th onClick={() => handleSort('planName')} style={{ cursor: 'pointer' }}>
                      Plan Name <span style={{ color: sortColumn === 'planName' ? '#333' : '#bbb' }}>{getSortArrow('planName')}</span>
                    </Th>
                    <Th onClick={() => handleSort('subscriptionDate')} style={{ cursor: 'pointer' }}>
                      Subscription Date <span style={{ color: sortColumn === 'subscriptionDate' ? '#333' : '#bbb' }}>{getSortArrow('subscriptionDate')}</span>
                    </Th>
                    <Th onClick={() => handleSort('dueDate')} style={{ cursor: 'pointer' }}>
                      Due Date <span style={{ color: sortColumn === 'dueDate' ? '#333' : '#bbb' }}>{getSortArrow('dueDate')}</span>
                    </Th>
                    <Th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                      Status <span style={{ color: sortColumn === 'status' ? '#333' : '#bbb' }}>{getSortArrow('status')}</span>
                    </Th>
                    <Th>Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLocations.map((location) => (
                    <tr key={location.id}>
                      <Td>{location.locationId}</Td>
                      <Td>{location.locationName}</Td>
                      <Td>{location.planName}</Td>
                      <Td>{location.subscriptionDate}</Td>
                      <Td>{location.dueDate}</Td>
                      <Td>
                        <StatusBadge status={location.status}>{location.status}</StatusBadge>
                        {location.isTrialActive && (
                          <div style={{
                            marginTop: '4px',
                            fontSize: '11px',
                            color: '#ff6b6b',
                            fontWeight: '500'
                          }}>
                            ⏰ Trial Active
                          </div>
                        )}
                      </Td>
                      <Td>
                        <TableActionGroup>
                          {isLocationRowCancelled(location.status) ? (
                            <TableActionButton
                              variant="resubscribe"
                              onClick={() => handleResubscribe(location)}
                            >
                              Re-subscribe
                            </TableActionButton>
                          ) : (
                            <>
                              {location.whatsappConnected ? (
                                <TableActionButton
                                  variant="disconnect"
                                  onClick={() => handleOpenWhatsAppDisconnect(location)}
                                  disabled={disconnectLoading}
                                >
                                  Disconnect WhatsApp
                                </TableActionButton>
                              ) : (
                                <TableActionButton
                                  variant="connect"
                                  onClick={() => navigate(`${APP_ROUTES.QR_SCAN}?locationId=${location.locationId}`)}
                                >
                                  Connect WhatsApp
                                </TableActionButton>
                              )}
                              <TableActionButton
                                variant="cancel"
                                onClick={() => handleOpenCancelModal(location)}
                              >
                                Cancel Subscription
                              </TableActionButton>
                            </>
                          )}
                        </TableActionGroup>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination>
                <PaginationControls>
                  <PaginationButton disabled>Previous</PaginationButton>
                  <PaginationButton active>1</PaginationButton>
                  <PaginationButton disabled>Next</PaginationButton>
                </PaginationControls>
              </Pagination>
            </>
          )}
        </PageContainer>
        )}

        <Footer isCollapsed={isCollapsed} />
      </MainContent>

      {cancelModalOpen && selectedSubscription && (
        <ModalOverlay>
          <Modal>
            <ModalHeader>
              <ModalTitle>Cancel Subscription?</ModalTitle>
              <CloseButton onClick={handleCloseCancelModal}>×</CloseButton>
            </ModalHeader>
            <div style={{ marginBottom: 16 }}>
              <strong style={{ color: '#dc3545' }}>Important:</strong> Billing for this sub-account will stop immediately.
              Your GHL connection stays saved — use <strong>Re-subscribe</strong> on the list anytime to activate again with a new plan.
            </div>
            {cancelError && <ErrorMessage>{cancelError}</ErrorMessage>}
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <CancelButton style={{ background: '#f5f6fa', color: '#333', border: '1px solid #ddd' }} onClick={handleCloseCancelModal} disabled={cancelLoading}>
                No, don't
              </CancelButton>
              <CancelButton onClick={handleCancelSubmit} disabled={cancelLoading}>
                {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
              </CancelButton>
            </div>
          </Modal>
        </ModalOverlay>
      )}



      {isLoadingAction && <Loader />}

      <LogoutConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />

      <WhatsAppLogoutDialog
        isOpen={whatsappDisconnectOpen}
        isLoading={disconnectLoading}
        onConfirm={handleConfirmWhatsAppDisconnect}
        onCancel={() => {
          if (disconnectLoading) return;
          setWhatsappDisconnectOpen(false);
          setLocationToDisconnect(null);
        }}
      />
    </DashboardContainer>
  );
};

export default SubscribedLocations; 